
import React, { useState } from 'react';
import { Moon, Sun, Bell, Eye, Palette, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { toast } from '@/utils/toast';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

const SettingsSection: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = theme === 'dark';
  
  // Local state for toggles
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [highContrastMode, setHighContrastMode] = useState(false);
  
  const handleToggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
    toast.success(notificationsEnabled ? "Notifications disabled" : "Notifications enabled");
  };
  
  const handleToggleHighContrast = () => {
    setHighContrastMode(!highContrastMode);
    toast.success(highContrastMode ? "High contrast mode disabled" : "High contrast mode enabled");
  };

  const handleCustomize = () => {
    toast.info("Customize options coming soon!");
  };

  if (!currentUser) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col items-center justify-center py-10 px-4 text-center"
      >
        <p className="text-muted-foreground mb-6">You need to be logged in to access settings</p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-2xl mx-auto"
    >
      <Card className={cn(
        "overflow-hidden rounded-xl border shadow-sm",
        isDark 
          ? "bg-gradient-to-b from-[#151833] to-[#1A1F38] border-blue-900/30" 
          : "bg-white border-blue-100/50"
      )}>
        <CardHeader>
          <h2 className={cn(
            "text-xl font-bold flex items-center gap-2",
            isDark ? "text-white" : "text-slate-800"
          )}>
            <Settings size={20} className={isDark ? "text-blue-400" : "text-blue-600"} />
            Settings
          </h2>
          <p className="text-sm text-muted-foreground">
            Customize your app preferences
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className={cn(
                "text-sm font-bold",
                isDark ? "text-white" : "text-slate-800"
              )}>
                Display
              </h3>
            </div>
            
            <div className={cn(
              "grid gap-4 rounded-lg p-4",
              isDark ? "bg-blue-900/10" : "bg-blue-50/70"
            )}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isDark ? (
                    <Moon size={18} className="text-blue-400" />
                  ) : (
                    <Sun size={18} className="text-amber-500" />
                  )}
                  <div>
                    <p className={cn(
                      "font-medium",
                      isDark ? "text-white" : "text-slate-800"
                    )}>
                      Theme Mode
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Switch between light and dark mode
                    </p>
                  </div>
                </div>
                
                <Switch
                  checked={isDark}
                  onCheckedChange={() => {
                    setTheme(isDark ? 'light' : 'dark');
                    toast.success(isDark ? "Light mode activated" : "Dark mode activated");
                  }}
                />
              </div>
              
              <Separator className={isDark ? "bg-blue-900/20" : "bg-blue-100/70"} />
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Palette size={18} className={isDark ? "text-blue-400" : "text-blue-600"} />
                  <div>
                    <p className={cn(
                      "font-medium",
                      isDark ? "text-white" : "text-slate-800"
                    )}>
                      Customize Appearance
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Personalize colors and UI elements
                    </p>
                  </div>
                </div>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCustomize}
                  className={cn(
                    "rounded-full",
                    isDark 
                      ? "border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20" 
                      : "border-blue-500/30 text-blue-600 hover:bg-blue-50"
                  )}
                >
                  Customize
                </Button>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className={cn(
                "text-sm font-bold mb-4",
                isDark ? "text-white" : "text-slate-800"
              )}>
                Notifications
              </h3>
              
              <div className={cn(
                "rounded-lg p-4",
                isDark ? "bg-blue-900/10" : "bg-blue-50/70"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell size={18} className={isDark ? "text-blue-400" : "text-blue-600"} />
                    <div>
                      <p className={cn(
                        "font-medium",
                        isDark ? "text-white" : "text-slate-800"
                      )}>
                        Notification Sounds
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Enable or disable notification sounds
                      </p>
                    </div>
                  </div>
                  
                  <Switch
                    checked={notificationsEnabled}
                    onCheckedChange={handleToggleNotifications}
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className={cn(
                "text-sm font-bold mb-4",
                isDark ? "text-white" : "text-slate-800"
              )}>
                Accessibility
              </h3>
              
              <div className={cn(
                "rounded-lg p-4",
                isDark ? "bg-blue-900/10" : "bg-blue-50/70"
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Eye size={18} className={isDark ? "text-blue-400" : "text-blue-600"} />
                    <div>
                      <p className={cn(
                        "font-medium",
                        isDark ? "text-white" : "text-slate-800"
                      )}>
                        High Contrast Mode
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Improve visual accessibility
                      </p>
                    </div>
                  </div>
                  
                  <Switch
                    checked={highContrastMode}
                    onCheckedChange={handleToggleHighContrast}
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SettingsSection;
